import { createAsyncThunk } from "@reduxjs/toolkit"

import { errorSave } from "../../../../redux/errors.slice"
import type { RootState } from "../../../../store.ts"
import { unknownError } from "../../../../utils/handlers/handle-error"
import i18n from "../../../../i18n.ts"
import { showResponseMessage } from "../../../../utils/handlers/show-response-message.ts"
import * as proposalServices from "../../../Proposals/api/services"
import * as services from "../../api/services"
import type {
  Document,
  DocumentRequestData
} from "../../types/document.types.ts"
import { documentTemplateSliceName } from "../document-templates.constants.ts"

export const fetchProposalDocumentById = createAsyncThunk<Document, string>(
  `${documentTemplateSliceName}/fetchProposalDocumentById`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.getProposalDocumentById(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response.data!
  }
)

export const createProposalDocument = createAsyncThunk<
  Document,
  DocumentRequestData
>(
  `${documentTemplateSliceName}/createProposalDocument`,
  async (data, { dispatch, rejectWithValue }) => {
    const response = await services.createProposalDocument(data)
    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Document created successfully.")

    return response!.data!.details.data
  }
)

export const updateProposalDocument = createAsyncThunk<
  Document,
  { id: string; data: Partial<Document> }
>(
  `${documentTemplateSliceName}/updateProposalDocument`,
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    const response = await services.updateProposalDocument(id, data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Document updated successfully.")

    return response!.data!.details.data
  }
)

export const saveProposalDocument = createAsyncThunk<void, string>(
  `${documentTemplateSliceName}/saveProposalDocument`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.saveProposalDocument(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Document saved successfully.")
  }
)

export const sendProposalDocumentToApproval = createAsyncThunk<
  void,
  string,
  { state: RootState }
>(
  `${documentTemplateSliceName}/sendProposalDocumentToApproval`,
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const saveRes = await services.saveProposalDocument(id)
      if (!saveRes.ok) {
        dispatch(errorSave(saveRes.data?.details))
        return rejectWithValue(null)
      }

      const attachmentId = (
        saveRes.data as unknown as { details?: { data?: { id?: string } } }
      )?.details?.data?.id
      if (!attachmentId) throw new Error()

      const submitRes = await proposalServices.submitForReview({
        proposalId: id,
        attachmentId
      })
      if (!submitRes.ok) {
        dispatch(errorSave((submitRes.data as { details?: unknown })?.details))
        return rejectWithValue(null)
      }

      showResponseMessage({}, i18n.t("documentApproval.documentSent"))
    } catch (e: unknown) {
      dispatch(errorSave(unknownError))
      return rejectWithValue(null)
    }
  }
)
